import { mergedMotionAlarmSchedule, type WeeklySchedule } from '@solinkcorp/solink-schedule';
import { type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import { useEffect, useState } from 'react';
import { type VideoDiscoverySite } from 'utils/types';

export interface UseMergedScheduleOptions {
  activeExceptions?: ScheduledActivityEntity[];
}

/**
 * Merges a location's base alarm schedule with active schedule exceptions.
 * Returns a location object with the merged schedule added to motionAlarmSchedule.mergedSchedule.
 *
 * @param location - The location to merge schedule for
 * @param options - Configuration options including activeExceptions
 * @returns Object containing locationWithMergedSchedule and loading state
 */
export function useMergedSchedule<L extends VideoDiscoverySite | undefined>(
  location: L,
  options: UseMergedScheduleOptions = {},
): {
  locationWithMergedSchedule: L;
  mergedScheduleLoading: boolean;
} {
  const { activeExceptions } = options;
  const [mergedSchedule, setMergedSchedule] = useState<WeeklySchedule | null>(null);
  const [mergedScheduleLoading, setMergedScheduleLoading] = useState(false);

  // Calculate merged schedule when activeExceptions change
  useEffect(() => {
    // Skip if no exceptions or location doesn't have required data
    if (
      !activeExceptions?.length ||
      !location?.timezone ||
      !location?.motionAlarmSchedule?.schedule
    ) {
      setMergedSchedule(null);
      setMergedScheduleLoading(false);
      return;
    }

    setMergedScheduleLoading(true);

    const calculateMergedSchedule = async () => {
      try {
        const merged = await mergedMotionAlarmSchedule(
          location.timezone,
          location.motionAlarmSchedule!.schedule,
          activeExceptions,
        );
        setMergedSchedule(merged);
      } catch (error) {
        console.error('Error calculating merged schedule:', error);
        setMergedSchedule(null);
      } finally {
        setMergedScheduleLoading(false);
      }
    };

    calculateMergedSchedule();
  }, [activeExceptions, location?.timezone, location?.motionAlarmSchedule]);

  // Create location with merged schedule for components that need it
  const locationWithMergedSchedule: L = (
    location && mergedSchedule
      ? {
          ...location,
          motionAlarmSchedule: location.motionAlarmSchedule
            ? {
                ...location.motionAlarmSchedule,
                mergedSchedule,
              }
            : undefined,
        }
      : location
  ) as L;

  return {
    locationWithMergedSchedule,
    mergedScheduleLoading,
  };
}
